import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FaUserCog, FaKey, FaUsers, FaTrash, FaPlus } from 'react-icons/fa';
import { toast } from 'sonner';
import { adminApi, getAdminUser, formatApiError } from '@/lib/adminAuth';

export const AdminAccountDialog = () => {
  const currentUser = getAdminUser();
  const [open, setOpen] = useState(false);

  // Change password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Manage admins
  const [admins, setAdmins] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newAccountPassword, setNewAccountPassword] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchAdmins = async () => {
    try {
      const { data } = await adminApi.get('/admin/users');
      setAdmins(data);
    } catch (err) {
      toast.error('Erreur lors du chargement des comptes');
    }
  };

  useEffect(() => {
    if (open) fetchAdmins();
  }, [open]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setPwLoading(true);
    try {
      const { data } = await adminApi.post('/admin/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(data.message || 'Mot de passe modifié');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await adminApi.post('/admin/users', {
        username: newUsername,
        password: newAccountPassword,
      });
      toast.success(data.message || 'Compte créé');
      setNewUsername('');
      setNewAccountPassword('');
      fetchAdmins();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Supprimer définitivement le compte "${username}" ?`)) return;
    try {
      const { data } = await adminApi.delete(`/admin/users/${username}`);
      toast.success(data.message || 'Compte supprimé');
      fetchAdmins();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="account-settings-btn"
          variant="outline"
          className="border-brand-secondary text-brand-secondary hover:bg-brand-secondary hover:text-white"
        >
          <FaUserCog className="mr-2" />
          Gérer les comptes
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-brand-secondary">Gestion des comptes administrateur</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="password" className="w-full mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="password" data-testid="tab-change-password">
              <FaKey className="mr-2" /> Mot de passe
            </TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-manage-accounts">
              <FaUsers className="mr-2" /> Comptes
            </TabsTrigger>
          </TabsList>

          {/* Change password */}
          <TabsContent value="password" className="mt-4">
            <p className="text-sm text-gray-500 mb-4">
              Connecté en tant que <strong>{currentUser}</strong>
            </p>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-gray-700">Mot de passe actuel</Label>
                <Input
                  type="password"
                  data-testid="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-700">Nouveau mot de passe</Label>
                <Input
                  type="password"
                  data-testid="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1"
                  minLength={6}
                  required
                />
              </div>
              <div>
                <Label className="text-gray-700">Confirmer le nouveau mot de passe</Label>
                <Input
                  type="password"
                  data-testid="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1"
                  minLength={6}
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="submit-change-password"
                disabled={pwLoading}
                className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white"
              >
                {pwLoading ? 'Modification...' : 'Modifier le mot de passe'}
              </Button>
            </form>
          </TabsContent>

          {/* Manage accounts */}
          <TabsContent value="accounts" className="mt-4">
            <div className="space-y-2 mb-6">
              <Label className="text-gray-700 font-semibold">Comptes existants</Label>
              {admins.map((a) => (
                <div
                  key={a.username}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border"
                  data-testid={`admin-row-${a.username}`}
                >
                  <div>
                    <span className="font-medium text-gray-800">{a.username}</span>
                    <span className="ml-2 text-xs bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-full">
                      {a.role}
                    </span>
                  </div>
                  <Button
                    onClick={() => handleDeleteAdmin(a.username)}
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-admin-${a.username}`}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <FaTrash />
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <Label className="text-gray-700 font-semibold flex items-center gap-2 mb-3">
                <FaPlus className="text-brand-primary" /> Ajouter un compte
              </Label>
              <form onSubmit={handleCreateAdmin} className="space-y-3">
                <Input
                  type="text"
                  data-testid="new-admin-username"
                  placeholder="Nom d'utilisateur"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  required
                />
                <Input
                  type="password"
                  data-testid="new-admin-password"
                  placeholder="Mot de passe (min. 6 caractères)"
                  value={newAccountPassword}
                  onChange={(e) => setNewAccountPassword(e.target.value)}
                  minLength={6}
                  required
                />
                <Button
                  type="submit"
                  data-testid="submit-create-admin"
                  disabled={creating}
                  className="w-full bg-brand-secondary hover:bg-brand-secondary/90 text-white"
                >
                  {creating ? 'Création...' : 'Créer le compte'}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
